/**
 * Tenant-admin views: overview, deployments, apps, users, config, IAM, backup, audit.
 *
 * Both languages live side by side so a review sees the pair in one diff.
 * `index.ts` merges this module into the catalogs under the `tenantViews` namespace.
 */
export const de = {
  common: {
    noTenant: 'Kein Mandant zugewiesen.',
    noTenantContactAdmin: 'Kein Mandant zugewiesen. Bitte kontaktieren Sie den Plattform-Administrator.',
    tenantNotFound: 'Mandant nicht gefunden.',
    active: 'Aktiv',
    disabled: 'Deaktiviert',
  },
  overview: {
    provisioning: 'Provisionierung',
    stepPrefix: 'Schritt',
    appsReady: 'Apps bereit',
    edition: 'Edition',
    domain: 'Domain',
    created: 'Erstellt',
    applications: 'Anwendungen',
    conditions: 'Bedingungen',
  },
  deployments: {
    title: 'Deployments',
    subtitle: 'Helm-Releases',
    empty: 'Keine Komponenten in diesem Mandanten bereitgestellt.',
    countSuffix: 'Komponenten',
    table: {
      name: 'Name',
      chart: 'Chart',
      version: 'Version',
      phase: 'Phase',
      enabled: 'Aktiviert',
    },
  },
  apps: {
    title: 'Apps',
    subtitle: 'Anwendungen',
    empty: 'Keine Anwendungen konfiguriert.',
    countSuffix: 'Anwendungen',
    table: {
      name: 'Name',
      version: 'Version',
      status: 'Status',
      enabled: 'Aktiviert',
    },
  },
  iam: {
    title: 'IAM & Auth',
    subtitle: 'Identitats- und Zugriffsverwaltung',
    realmCardTitle: 'Keycloak Realm',
    realmName: 'Realm-Name',
    domain: 'Domain',
    passwordPolicyCardTitle: 'Passwort-Richtlinie',
    mfaCardTitle: 'Multi-Faktor-Authentifizierung',
    mfaEmpty: 'Keine MFA-Methoden konfiguriert.',
  },
  backup: {
    title: 'Backup',
    subtitle: 'Sicherungen',
    startButton: 'Backup starten',
    started: 'Backup gestartet.',
    failedPrefix: 'Backup fehlgeschlagen:',
    unknownError: 'Unbekannter Fehler',
    empty: 'Keine Sicherungen vorhanden. Erstellen Sie ein Backup mit dem Button oben.',
    table: {
      name: 'Name',
      type: 'Typ',
      phase: 'Phase',
      size: 'Größe',
      lastBackup: 'Letzte Sicherung',
    },
  },
  audit: {
    title: 'Audit',
    subtitle: 'Audit-Protokoll (BSI OPS.1.1.5)',
    empty: 'Keine Audit-Einträge vorhanden. Operator-Aktionen werden in Anlehnung an BSI OPS.1.1.5 in den strukturierten Logs protokolliert und hier angezeigt, sobald Ereignisse für diesen Mandanten auftreten.',
    table: {
      timestamp: 'Zeitpunkt',
      actor: 'Akteur',
      action: 'Aktion',
      resource: 'Ressource',
      outcome: 'Ergebnis',
      detail: 'Detail',
    },
  },
} as const;

export const en = {
  common: {
    noTenant: 'No tenant assigned.',
    noTenantContactAdmin: 'No tenant assigned. Contact the platform administrator.',
    tenantNotFound: 'Tenant not found.',
    active: 'Active',
    disabled: 'Disabled',
  },
  overview: {
    provisioning: 'Provisioning',
    stepPrefix: 'Step',
    appsReady: 'Apps ready',
    edition: 'Edition',
    domain: 'Domain',
    created: 'Created',
    applications: 'Applications',
    conditions: 'Conditions',
  },
  deployments: {
    title: 'Deployments',
    subtitle: 'Helm releases',
    empty: 'No components deployed in this tenant.',
    countSuffix: 'components',
    table: {
      name: 'Name',
      chart: 'Chart',
      version: 'Version',
      phase: 'Phase',
      enabled: 'Enabled',
    },
  },
  apps: {
    title: 'Apps',
    subtitle: 'Applications',
    empty: 'No applications configured.',
    countSuffix: 'applications',
    table: {
      name: 'Name',
      version: 'Version',
      status: 'Status',
      enabled: 'Enabled',
    },
  },
  iam: {
    title: 'IAM & Auth',
    subtitle: 'Identity and access management',
    realmCardTitle: 'Keycloak realm',
    realmName: 'Realm name',
    domain: 'Domain',
    passwordPolicyCardTitle: 'Password policy',
    mfaCardTitle: 'Multi-factor authentication',
    mfaEmpty: 'No MFA methods configured.',
  },
  backup: {
    title: 'Backup',
    subtitle: 'Backups',
    startButton: 'Start backup',
    started: 'Backup started.',
    failedPrefix: 'Backup failed:',
    unknownError: 'Unknown error',
    empty: 'No backups yet. Create one with the button above.',
    table: {
      name: 'Name',
      type: 'Type',
      phase: 'Phase',
      size: 'Size',
      lastBackup: 'Last backup',
    },
  },
  audit: {
    title: 'Audit',
    subtitle: 'Audit log (BSI OPS.1.1.5)',
    empty: 'No audit entries yet. Operator actions are logged in the structured logs, modelled on BSI OPS.1.1.5, and appear here once events occur for this tenant.',
    table: {
      timestamp: 'Timestamp',
      actor: 'Actor',
      action: 'Action',
      resource: 'Resource',
      outcome: 'Outcome',
      detail: 'Detail',
    },
  },
} as const;
